<template>  
  <Form as="div" ref="Form">
    <transition v-if="showCallMe" name="fade">
      <div v-if="viewModel">
        <page-spinner :spinner-url-list="spinnerUrlList" />
        <div v-if="showForm">
          <div class="control push-down">
            <label data-test-callme-first-name-label for="callme-firstname">
              <span v-html="viewModel.firstNameLabel" />
              <tooltip v-if="phoneLocked" :content="viewModel.firstNameTooltip" />
            </label>
            <Field
              v-model="viewModel.submitTemplate.firstName"
              :rules="'required'"
              type="text"
              name="callme-firstname"
              id="callme-firstname"
              :label="viewModel.firstNameLabel"
            />
            <ValidationError name="callme-firstname" />
            <label data-test-callme-last-name-label for="callme-lastname">
              <span v-html="viewModel.lastNameLabel" />
              <tooltip v-if="phoneLocked" :content="viewModel.lastNameTooltip" />
            </label>
            <Field
              v-model="viewModel.submitTemplate.lastName"
              :rules="'required'"
              type="text"
              name="callme-lastname"
              id="callme-lastname"
              :label="viewModel.lastNameLabel"
            />
            <ValidationError name="callme-lastname" />
            <label data-test-callme-phone-label for="callme-phone">
              <span v-html="viewModel.phoneLabel" />
              <tooltip v-if="phoneLocked" :content="viewModel.phoneTooltip" />
            </label>
            <Field
              v-model="viewModel.submitTemplate.phone"
              :rules="'required|numeric|min:8|max:8'"
              type="text"
              name="callme-phone"
              id="callme-phone"
              :label="viewModel.phoneLabel"
              :disabled="phoneLocked"
            />
            <ValidationError name="callme-phone" />
            <div v-if="showEmail">
              <label data-test-callme-email-label for="callme-email">
                <span v-html="viewModel.emailLabel" />
                <tooltip v-if="phoneLocked" :content="viewModel.emailTooltip" />
              </label>
              <Field v-model="viewModel.submitTemplate.email" type="text" name="callme-email" id="callme-email" />
            </div>
            <div v-if="showTextField">
              <label data-test-callme-body-label for="callme-text">
                <span v-html="viewModel.bodyLabel" />
                <span v-text="` (max ${computedBodyMaxLength} tegn)`" />
                <tooltip v-if="phoneLocked" :content="viewModel.bodyTooltip" />
              </label>
              <textarea
                v-model="viewModel.submitTemplate.body"
                type="text"
                name="callme-text"
                id="callme-text"
                rows="4"
                :maxlength="computedBodyMaxLength"
              />
            </div>
          </div>
          <div class="control">
            <button
              v-if="!viewModel.bookingEnabled"
              class="button large"
              data-test-callme-button
              @click="submitCallMe(viewModel.submitTemplate)"
              v-html="viewModel.buttonSubmitLabel"
            />
            <button
              v-if="viewModel.bookingEnabled"
              class="button large"
              data-test-callme-button
              @click="showBooking"
              v-html="viewModel.buttonBookingLabel"
            />
          </div>
        </div>
        <div v-if="showReply">
          <p v-html="viewModel.replyText" />
        </div>
        <div v-if="showError">
          <p v-html="viewModel.errorText" />
        </div>
        <div v-if="showCallMeBookingChoice">
          <call-me-booking-choice
            v-model="bookingChoiceASAP"
            :title="viewModel.bookingStepOneTitle"
            :question-asap="viewModel.bookingStepOneASAPLabel"
            :question-choose="viewModel.bookingStepOneChooseTimeLabel"
            :button-label="viewModel.bookingStepOneButtonLabel"
            @booking-choice="submitCallMeAfterBookingChoice"
          />
        </div>
        <div v-if="showCallMeBookingDate">
          <call-me-booking-date
            v-if="!viewModel.isSalesForceTimeSlotsEnabled"
            v-model="viewModel.submitTemplate.handleNow"
            :title="viewModel.bookingStepTwoTitle"
            :description="viewModel.bookingStepTwoDescription"
            :date-label="viewModel.bookingStepTwoDateLabel"
            :time-label="viewModel.bookingStepTwoTimeLabel"
            :interval="viewModel.bookingStepTwoInterval"
            :button-label="viewModel.bookingStepTwoButtonLabel"
            :open-hours="viewModel.bookingOpeningHours"
            @booking-date="submitCallMeAfterBookingDate"
          />
          <call-me-booking-date-salesforce
            v-if="viewModel.isSalesForceTimeSlotsEnabled"
            v-model="viewModel.submitTemplate.handleNow"
            :title="viewModel.bookingStepTwoTitle"
            :description="viewModel.bookingStepTwoDescription"
            :date-label="viewModel.bookingStepTwoDateLabel"
            :time-label="viewModel.bookingStepTwoTimeLabel"
            :button-label="viewModel.bookingStepTwoButtonLabel"
            :timeslot-validation-error="viewModel.bookingStepTwoTimeslotValidationError"
            @booking-date="submitCallMeAfterBookingDate"
          />
        </div>
        <div v-if="showCallMeBookingReceipt">
          <call-me-booking-receipt
            :title="viewModel.bookingReceiptTitle"
            :description="bookingReceiptDescription"
            :close-button-label="viewModel.bookingReceiptCloseButtonLabel"
            @booking-close="close"
          />
        </div>
      </div>
    </transition>
  </Form>
</template>

<script lang="ts">
import {
  CallMeData,
  CallMeViewModel,
  bookingDateSubmitParams,
  SubmitTemplate,
  SalesForceOpeningDays
} from './types/CallMe';
import CallMeBookingChoice from './CallMeBookingChoice.vue';
import CallMeBookingDate from './CallMeBookingDate.vue';
import CallMeBookingDateSalesforce from './CallMeBookingDateSalesforce.vue';
import CallMeBookingReceipt from './CallMeBookingReceipt.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import PageSpinner from '@/components/UI/PageSpinner.vue';
import Tooltip from '@/components/UI/Tooltip.vue';
import RequestService from '@/common/requestService';
import Utility from '@/common/UtilityFunctions.js';
import moment from 'moment';
import { defineComponent } from 'vue';
import { Form, ValidationResult } from 'vee-validate';

export default defineComponent({
  name: 'CallMe',
  components: {
    ValidationError,
    PageSpinner,
    Tooltip,
    CallMeBookingChoice,
    CallMeBookingDate,
    CallMeBookingReceipt,
    CallMeBookingDateSalesforce
  },
  props: {
    datasource: {
      type: String,
      default() {
        return '';
      }
    }
  },
  data(): CallMeData {
    return {
      viewModel: null,
      showForm: false,
      showReply: false,
      showError: false,
      showCallMeBookingChoice: false,
      showCallMeBookingDate: false,
      showCallMeBookingReceipt: false,
      bookingChoiceASAP: null,
      phoneLocked: false,
      timeslotsSalesforce: null,
    };
  },
  computed: {
    spinnerUrlList() {
      return [Utility.constants().API.lead_submit, Utility.constants().API.salesforce_timeslots_get];
    },
    showCallMe(): boolean {
      return this.viewModel !== null;
    },
    showEmail(): boolean {
      return this.viewModel?.emailLabel !== '';
    },
    showTextField(): boolean {
      return this.viewModel?.bodyLabel !== '';
    },
    computedBodyMaxLength(): string | undefined {
      return this.viewModel?.bodyMaxLength === '' ? '1000' : this.viewModel?.bodyMaxLength;
    },
    bookingReceiptDescription(): string {
      if (
        this.viewModel?.submitTemplate.contactDate !== null &&
        this.viewModel?.submitTemplate.contactHours !== null
      ) {
        const dateString = moment(this.viewModel?.submitTemplate.contactDate).format('LL');
        return `Vi ringer dig op ${dateString} mellem ${this.viewModel?.submitTemplate?.contactHours?.label}`;
      }
      if (this.viewModel?.bookingReceiptDescription) {
        return this.viewModel?.bookingReceiptDescription;
      }
      return '';
    }
  },
  beforeMount() {
    this.getViewModel();
  },
  methods: {
    async getViewModel(): Promise<void> {
      await RequestService.get<CallMeViewModel>(
        `${Utility.constants().API.callme_get}/${this.datasource}`
      )
        .then((response) => {
          const data = response.data;
          this.viewModel = data;
          this.showForm = true;
          this.$emit('call-me-loaded', true);
          if (this.viewModel?.submitTemplate.phone) {
            this.phoneLocked = true;
          }
          if(this.viewModel?.isSalesForceTimeSlotsEnabled) {
            this.getTimeSlotsFromSalesforce();
          }
        })
        .catch((err) => {
          Utility.logError(err, this);
          this.viewModel = null;
          this.$emit('call-me-not-loaded');
        });
    },
    async getTimeSlotsFromSalesforce(): Promise<void> {
      await RequestService.get<SalesForceOpeningDays>(Utility.constants().API.salesforce_timeslots_get)
        .then((response: { data: SalesForceOpeningDays; }) => {
          this.timeslotsSalesforce = response.data;
        })
        .catch((err: any) => {
          Utility.logError(err, this);
          this.timeslotsSalesforce = { dates: [] };
        });
    },
    showBooking(): void {
      (this.$refs.Form as typeof Form).validate().then((result: ValidationResult) => {
        if (!result.valid) return;

        this.dispatchTrackingEvent('Bestil Opkald', 'Klik Bestil opkald', 'Start form');

        const isSalesforceEnabled = this.viewModel?.isSalesForceTimeSlotsEnabled;
        const hasSalesforceTimeSlots = this.timeslotsSalesforce && this.timeslotsSalesforce.dates && this.timeslotsSalesforce.dates.length > 0;

        if (!isSalesforceEnabled || (isSalesforceEnabled && hasSalesforceTimeSlots)) {
          this.showForm = false;
          this.viewModel?.skipBookingStepOne ? this.showCallMeBookingDate = true : this.showCallMeBookingChoice = true;
          this.$emit('call-me-step-two');
          return;
        }

        if (this.viewModel && this.timeslotsSalesforce) {
          this.submitCallMe(this.viewModel.submitTemplate);
        }
      });
    },
    submitCallMe(template: SubmitTemplate): void {
      (this.$refs.Form as typeof Form).validate().then((result: ValidationResult) => {
        if (!result.valid) return;

        if (!this.viewModel?.bookingEnabled) {
          this.dispatchTrackingEvent('Bestil Opkald', 'Klik Bestil opkald - submit step 1', 'Hurtigst muligt');
        }

        RequestService.put<void>(Utility.constants().API.lead_submit, template)
          .then(() => {
            this.$emit('call-me-reply');
            if (this.showCallMeBookingChoice || this.showCallMeBookingDate) {
              this.showCallMeBookingChoice = false;
              this.showCallMeBookingDate = false;
              this.showCallMeBookingReceipt = true;
            } else {
              this.showForm = false;
              this.showReply = true;
            }
          })
          .catch((err) => {
            Utility.logError(err, this);
            this.showForm = false;
            this.showCallMeBookingChoice = false;
            this.showError = true;
            this.$emit('call-me-reply');
          });
      });
    },
    submitCallMeAfterBookingChoice(): void {
      if (this.bookingChoiceASAP) {
        if (this.viewModel && this.viewModel.submitTemplate) {
          this.dispatchTrackingEvent('Bestil Opkald', 'Klik Bestil opkald - submit', 'Hurtigst muligt');
          this.submitCallMe(this.viewModel.submitTemplate);
        }
      } else {
        this.dispatchTrackingEvent('Bestil Opkald', 'Klik Næste', 'Vælg tidspunkt');
        this.showCallMeBookingChoice = false;
        this.showCallMeBookingDate = true;
      }
    },
    submitCallMeAfterBookingDate(param: bookingDateSubmitParams): void {
      this.dispatchTrackingEvent('Bestil Opkald', 'Klik Bestil opkald - submit', 'Specifikt tidspunkt');
      if (this.viewModel && this.viewModel.submitTemplate) {
        this.viewModel.submitTemplate.handleNow = false;
        this.viewModel.submitTemplate.contactDate = moment(param.date).format('YYYY-MM-DD');
        this.viewModel.submitTemplate.contactHours = param.time;
        this.submitCallMe(this.viewModel.submitTemplate);
      }
    },
    close(): void {
      this.$emit('call-me-close');
    },
    dispatchTrackingEvent(eventCategory: string, eventAction: string, eventLabel: string): void {
      Utility.pushToTagManager({
        event: 'track-event',
        eventCategory,
        eventAction,
        eventLabel,
        eventValue: '0',
        eventNoninteraction: true
      });
    }
  }
});
</script>
